import React from 'react';
import { Book, Code2, Server, Shield, Terminal, GitBranch, Database, Cloud, ArrowRight } from 'lucide-react';

const Documentation = () => {
  const sections = [
    {
      icon: <Code2 className="w-8 h-8 text-yellow-400" />,
      title: "API Documentation",
      description: "Complete API reference, authentication guides, and endpoint documentation.",
      links: [
        { title: "Getting Started", url: "#" },
        { title: "Authentication", url: "#" },
        { title: "API Reference", url: "#" },
        { title: "Rate Limits", url: "#" }
      ]
    },
    {
      icon: <Book className="w-8 h-8 text-yellow-400" />,
      title: "Product Documentation",
      description: "User guides and implementation documentation for all Stinger Builds products.",
      links: [
        { title: "SureTrack CRM Guide", url: "#" },
        { title: "Device Update Manual", url: "#" },
        { title: "Drive the Demand Setup", url: "#" },
        { title: "Standup Feed Documentation", url: "#" }
      ]
    },
    {
      icon: <Terminal className="w-8 h-8 text-yellow-400" />,
      title: "Development Resources",
      description: "SDK documentation, code examples, and implementation best practices.",
      links: [
        { title: "SDK Overview", url: "#" },
        { title: "Code Examples", url: "#" },
        { title: "Best Practices", url: "#" },
        { title: "Migration Guides", url: "#" }
      ]
    },
    {
      icon: <Server className="w-8 h-8 text-yellow-400" />,
      title: "Platform Guides",
      description: "Infrastructure setup, deployment guides, and security best practices.",
      links: [
        { title: "Infrastructure Setup", url: "#" },
        { title: "Deployment Guide", url: "#" },
        { title: "Security Guidelines", url: "#" },
        { title: "Performance Optimization", url: "#" }
      ]
    }
  ];

  const quickLinks = [
    {
      icon: <GitBranch className="w-6 h-6" />,
      title: "Version Control",
      description: "Learn about our versioning system"
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Data Management",
      description: "Database setup and maintenance"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Security",
      description: "Security best practices and compliance"
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Cloud Integration",
      description: "Cloud deployment and scaling"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
        </div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Documentation</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Everything you need to know about our products, APIs, and development resources.
            </p>
            <div className="mt-8">
              <div className="relative max-w-xl mx-auto">
                <input
                  type="text"
                  placeholder="Search documentation..."
                  className="w-full px-6 py-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <a
                key={index}
                href="#"
                className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all group"
              >
                <div className="p-3 bg-slate-100 rounded-lg group-hover:bg-yellow-400/10">
                  {link.icon}
                </div>
                <div className="ml-4">
                  <h3 className="font-semibold">{link.title}</h3>
                  <p className="text-sm text-gray-600">{link.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Documentation Sections */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {sections.map((section, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                <div className="mb-6">{section.icon}</div>
                <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                <p className="text-gray-600 mb-8">{section.description}</p>
                <ul className="space-y-4">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <a
                        href={link.url}
                        className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors"
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        {link.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Need Help?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
            >
              Contact Support
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Documentation;